import { useQuery } from '@tanstack/react-query';
import { Server, Box, Globe, Rocket, Terminal, Users, Activity, RefreshCw, AlertCircle, X, ChevronRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { dashboardApi } from '../lib/api/dashboard';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export function DashboardPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  const { data: stats, isLoading: statsLoading, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const { data: recentDeployments, refetch: refetchDeployments } = useQuery({
    queryKey: ['recent-deployments'],
    queryFn: () => dashboardApi.getRecentDeployments(5),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const { data: serversHealth, refetch: refetchServers } = useQuery({
    queryKey: ['servers-health'],
    queryFn: () => dashboardApi.getServersHealth(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt));
    }
  }, [dataUpdatedAt]);

  const handleManualRefresh = async () => {
    setLastUpdated(new Date());
    await Promise.all([refetchDeployments(), refetchServers()]);
  };

  // Get time ago string
  const getTimeAgo = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const [timeAgo, setTimeAgo] = useState(getTimeAgo());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo());
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  // Generate alerts based on data
  const alerts: Array<{
    id: string;
    type: 'error' | 'warning' | 'info';
    message: string;
    action?: { label: string; link: string };
  }> = [];

  if (serversHealth) {
    serversHealth.forEach((server: any) => {
      const alertId = `server-${server.id}`;
      if (!dismissedAlerts.has(alertId)) {
        if (server.status === 'offline') {
          alerts.push({
            id: alertId,
            type: 'error' as const,
            message: `Server "${server.name}" is offline`,
            action: { label: 'View Server', link: `/servers/${server.id}` },
          });
        }
      }
    });
  }

  const statCards = [
    {
      title: 'Servers',
      value: stats?.servers.total || 0,
      icon: Server,
      color: 'bg-blue-100 text-blue-600',
      link: '/servers',
    },
    {
      title: 'Applications',
      value: stats?.apps.total || 0,
      subtitle: `${stats?.apps.active || 0} active`,
      icon: Box,
      color: 'bg-green-100 text-green-600',
      link: '/apps',
    },
    {
      title: 'Domains',
      value: stats?.domains.total || 0,
      icon: Globe,
      color: 'bg-purple-100 text-purple-600',
      link: '/domains',
    },
    {
      title: 'Deployments',
      value: stats?.deployments.last_7_days || 0,
      subtitle: 'Last 7 days',
      icon: Rocket,
      color: 'bg-orange-100 text-orange-600',
      link: '/apps',
    },
    {
      title: 'Terminal Sessions',
      value: stats?.terminal_sessions.active || 0,
      subtitle: 'Active now',
      icon: Terminal,
      color: 'bg-gray-100 text-gray-600',
      link: '/terminal',
    },
    {
      title: 'Users',
      value: stats?.users.total || 0,
      icon: Users,
      color: 'bg-indigo-100 text-indigo-600',
      link: '/users',
    },
  ];

  const deploymentStatusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    success: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    rolled_back: 'bg-yellow-100 text-yellow-700',
  };

  const dismissAlert = (alertId: string) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Overview of your infrastructure</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin-slow' : ''}`} />
              <span>Last updated: {timeAgo}</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="gap-2"
          >
            <div className={`h-2 w-2 rounded-full ${autoRefresh ? 'bg-green-500' : 'bg-gray-400'}`} />
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {alerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-orange-900">
                    {alerts.length} {alerts.length === 1 ? 'item needs' : 'items need'} attention
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => alerts.forEach(a => dismissAlert(a.id))}
                    className="text-orange-600 hover:text-orange-700"
                  >
                    Dismiss All
                  </Button>
                </div>
                <div className="space-y-2">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-2 bg-white rounded border border-orange-200">
                      <span className="text-sm text-gray-900">{alert.message}</span>
                      <div className="flex items-center gap-2">
                        {alert.action && (
                          <Link to={alert.action.link}>
                            <Button variant="outline" size="sm">
                              {alert.action.label}
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissAlert(alert.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="hover:border-primary-300 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.color} group-hover:scale-110 transition-transform`}>
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statsLoading ? '-' : stat.value}
                    </p>
                    {stat.subtitle && (
                      <p className="text-xs text-gray-400">{stat.subtitle}</p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Deployments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5" />
              Recent Deployments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentDeployments?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent deployments</p>
            ) : (
              <div className="space-y-3">
                {recentDeployments?.map((deployment) => (
                  <div
                    key={deployment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{deployment.app_name}</p>
                      <p className="text-sm text-gray-500">{deployment.version}</p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded-full ${
                          deploymentStatusColors[deployment.status] || 'bg-gray-100'
                        }`}
                      >
                        {deployment.status}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(deployment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Server Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Server Health
              </div>
              <Button variant="ghost" size="sm" onClick={() => refetchServers()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serversHealth?.length === 0 ? (
              <div className="text-center py-8">
                <Server className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-3">No servers configured</p>
                <Link to="/servers/new">
                  <Button size="sm">Add Your First Server</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {serversHealth?.map((server: any) => (
                  <Link
                    key={server.id}
                    to={`/servers/${server.id}`}
                    className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-primary-200"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`h-3 w-3 rounded-full ${
                              server.status === 'online'
                                ? 'bg-green-500 animate-pulse'
                                : 'bg-red-500'
                            }`}
                          />
                          <div>
                            <p className="font-medium text-gray-900">{server.name}</p>
                            <p className="text-xs text-gray-500">{server.host}</p>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>

                      {server.status === 'online' ? (
                        <div className="space-y-1.5 pl-6">
                          {server.load_average && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 w-16">CPU:</span>
                              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${
                                    parseFloat(server.load_average) > 80
                                      ? 'bg-red-500'
                                      : parseFloat(server.load_average) > 60
                                      ? 'bg-yellow-500'
                                      : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(parseFloat(server.load_average) || 0, 100)}%` }}
                                />
                              </div>
                              <span className="text-gray-600 w-10 text-right">{server.load_average}</span>
                            </div>
                          )}
                          {server.memory_usage && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 w-16">Memory:</span>
                              <span className="text-gray-600 flex-1">{server.memory_usage}</span>
                            </div>
                          )}
                          {server.disk_usage && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 w-16">Disk:</span>
                              {(() => {
                                const pct = parseFloat((server.disk_usage.match(/\((\d+)%\)/) || [])[1] || '0');
                                return (
                                  <>
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className={`h-1.5 rounded-full transition-all ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                        style={{ width: `${Math.min(pct, 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-gray-600 w-10 text-right">{server.disk_usage.match(/\((\d+%)\)/)?.[1] ?? server.disk_usage}</span>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="pl-6">
                          <p className="text-xs text-red-500 font-medium">Offline - Unable to connect</p>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
